import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { IPanoOptions } from "../../models/storeModels/IPanoOptions";
import { logout } from "../../features/auth";
import { getItemWebStorage } from "../../features/webStorageSaver";

function getInitialState() {
    const initialState: IPanoOptions = {
        panoramaFilters: null,
        reduxMoveRotate: true,
        xml: null,
        xmlChangeMarker: false,
        lookAngle: '0',
        panoramaDirectionFilter: getItemWebStorage('panoramaDirectionFilter') ?? "Все",
        panoramaSeasonFilter: getItemWebStorage('panoramaSeasonFilter') ?? "0",
        panoramaStep: getItemWebStorage('panoramaStep') ?? "50 м",
        panoramaYearFilter: getItemWebStorage('panoramaYearFilter') ?? "0",
        cameraDirectionsArray: [],
        hasNextFrame: false,
        hasPreviousFrame: false,
        cameraImageIsRendering: false,
        panoramaXMLIsRendering: false,
        panoramaCurrentHLookAngle: getItemWebStorage("panoramaHAngle") ?? 0,
        panoramaCurrentVLookAngle: getItemWebStorage("panoramaVAngle") ?? 0,
        panoramaCurrentFOV: getItemWebStorage("panoramaFOV") ?? 90,
        panoramaCurrentAzimuth: getItemWebStorage("panoramaAzimuth") ?? 0,
        panoramaCurrentPositionID: '',
        panoramaCurrentVideoDocumentID: '',
        panoramaCurrentDirection: '',
        panoramaCurrentPathName: '',
        panoramaCurrentRoadName: '',
        panoramaCurrentKm: '',
        panoramaCurrentKmPlus: '',
        panoramaCurrentDate: '',
        cameraMarkerLongitude: getItemWebStorage('cameraMarkerLongitude'),
        cameraMarkerLatitude: getItemWebStorage('cameraMarkerLatitude'),
        panoramaXMLIsLoading: false,
        panoramaVideoIsPlaying: false,
        videoCurrentDirection: '',
        panoramaWindowIsLoading: false,
        showCameras: false
    }
    return initialState
}

export const panoramaSlice = createSlice({
    name: 'panorama',
    initialState: getInitialState(),
    reducers: {
        setXML(state, action: PayloadAction<any>) {
            state.xml = action.payload
        },
        setXMLChangeMarker(state, action: PayloadAction<any>) {
            state.xmlChangeMarker = action.payload
        },
        setPanoramaFilters(state, action: PayloadAction<any>) {
            state.panoramaFilters = action.payload
        },
        setLookAngle(state, action: PayloadAction<any>) {
            state.lookAngle = action.payload
        },
        setPanoramaYearFilter(state, action: PayloadAction<string>) {
            state.panoramaYearFilter = action.payload
        },
        setPanoramaCurrentAzimuth(state, action: PayloadAction<any>) {
            state.panoramaCurrentAzimuth = action.payload
        },
        setPanoramaSeasonFilter(state, action: PayloadAction<string>) {
            state.panoramaSeasonFilter = action.payload
        },
        setPanoramaDirectionFilter(state, action: PayloadAction<string>) {
            state.panoramaDirectionFilter = action.payload
        },
        setPanoramaStep(state, action: PayloadAction<string>) {
            state.panoramaStep = action.payload
        },
        setCameraDirectionsArray(state, action: PayloadAction<string>) {
            state.cameraDirectionsArray = action.payload
        },
        setReduxMoveRotate(state, action: PayloadAction<boolean>) {
            state.reduxMoveRotate = action.payload
        },
        setCameraImageIsRendering(state, action: PayloadAction<boolean>) {
            state.cameraImageIsRendering = action.payload
        },
        setPanoramaXMLIsRendering(state, action: PayloadAction<boolean>) {
            state.panoramaXMLIsRendering = action.payload
        },
        setPanoramaVideoIsPlaying(state, action: PayloadAction<boolean>) {
            state.panoramaVideoIsPlaying = action.payload
        },
        setVideoCurrentDirection(state, action: PayloadAction<string>) {
            state.videoCurrentDirection = action.payload
        },
        setCameraFullscreenFrameUrl(state, action: PayloadAction<any>) {
            state.cameraFullscreenFrameUrl = action.payload
        },
        setPanoramaCurrentHLookAngle(state, action: PayloadAction<any>) {
            state.panoramaCurrentHLookAngle = action.payload
        },
        setPanoramaCurrentVLookAngle(state, action: PayloadAction<string>) {
            state.panoramaCurrentVLookAngle = action.payload
        },
        setPanoramaCurrentFOV(state, action: PayloadAction<string>) {
            state.panoramaCurrentFOV = action.payload
        },
        setPanoramaCurrentPositionID(state, action: PayloadAction<string>) {
            state.panoramaCurrentPositionID = action.payload
        },
        setPanoramaCurrentVideoDocumentID(state, action: PayloadAction<string>) {
            state.panoramaCurrentVideoDocumentID = action.payload
        },
        setPanoramaCurrentDirection(state, action: PayloadAction<string>) {
            state.panoramaCurrentDirection = action.payload
        },
        setPanoramaCurrentPathName(state, action: PayloadAction<string>) {
            state.panoramaCurrentPathName = action.payload
        },
        setPanoramaCurrentRoadName(state, action: PayloadAction<string>) {
            state.panoramaCurrentRoadName = action.payload
        },
        setPanoramaCurrentKm(state, action: PayloadAction<string>) {
            state.panoramaCurrentKm = action.payload
        },
        setPanoramaCurrentKmPlus(state, action: PayloadAction<string>) {
            state.panoramaCurrentKmPlus = action.payload
        },
        setPanoramaCurrentDate(state, action: PayloadAction<string>) {
            state.panoramaCurrentDate = action.payload
        },
        setCameraMarkerLongitude(state, action: PayloadAction<any>) {
            state.cameraMarkerLongitude = action.payload
        },
        setCameraMarkerLatitude(state, action: PayloadAction<any>) {
            state.cameraMarkerLatitude = action.payload
        },
        setPanoramaXMLIsLoading(state, action: PayloadAction<boolean>) {
            state.panoramaXMLIsLoading = action.payload
        },
        setPanoramaWindowIsLoading(state, action: PayloadAction<boolean>) {
            state.panoramaWindowIsLoading = action.payload
        },
        setHasNextFrame(state, action: PayloadAction<any>) {
            state.hasNextFrame = action.payload
        },
        setHasPreviousFrame(state, action: PayloadAction<boolean>) {
            state.hasPreviousFrame = action.payload
        },
        setShowCameras(state, action: PayloadAction<boolean>) {
            state.showCameras = action.payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => {
            return getInitialState()
        })
    },
})

export const {
    setXML,
    setXMLChangeMarker,
    setLookAngle,
    setPanoramaFilters,
    setReduxMoveRotate,
    setPanoramaDirectionFilter,
    setPanoramaSeasonFilter,
    setPanoramaYearFilter,
    setPanoramaStep,
    setPanoramaCurrentAzimuth,
    setCameraDirectionsArray,
    setCameraImageIsRendering,
    setPanoramaXMLIsRendering,
    setCameraFullscreenFrameUrl,
    setPanoramaCurrentHLookAngle,
    setPanoramaCurrentVLookAngle,
    setPanoramaCurrentFOV,
    setPanoramaCurrentDate,
    setPanoramaCurrentDirection,
    setPanoramaCurrentKm,
    setPanoramaCurrentKmPlus,
    setPanoramaCurrentPathName,
    setPanoramaCurrentPositionID,
    setPanoramaCurrentRoadName,
    setPanoramaCurrentVideoDocumentID,
    setCameraMarkerLatitude,
    setCameraMarkerLongitude,
    setPanoramaXMLIsLoading,
    setHasNextFrame,
    setHasPreviousFrame,
    setPanoramaVideoIsPlaying,
    setVideoCurrentDirection,
    setShowCameras,
    setPanoramaWindowIsLoading
} = panoramaSlice.actions
export const panoramaReducer = panoramaSlice.reducer;
